import React, { useState, useEffect } from "react";
import { BaseDialog } from "../../shared/BaseDialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Button } from "@shared/ui/button";
import { FormService, FormField } from "@shared/services/FormServices";
import { Info } from "lucide-react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { toSnakeCase } from "@shared/utils/caseConverter";

interface RemoveSubstringTransformationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetField: string;
  fileData: string[];
  onApplyTransformation: (config: any) => void;
}

const RemoveSubstringTransformationDialog: React.FC<
  RemoveSubstringTransformationDialogProps
> = ({ isOpen, onClose, targetField, fileData, onApplyTransformation }) => {
  const [formData, setFormData] = useState({
    textToRemove: "",
    matchType: "exact",
  });

  const [previewData, setPreviewData] = useState<{
    before: string[];
    after: string[];
  }>({
    before: [],
    after: [],
  });

  useEffect(() => {
    if (fileData) {
      const samples = fileData.filter((s) => s && s !== "...").slice(0, 2);
      setPreviewData({ before: samples, after: samples });
    }
  }, [fileData]);

  const fields: FormField[] = [
    {
      name: "textToRemove",
      type: "text",
      label: "Text to remove",
      placeholder: "Enter text to remove",
      className: "col-span-1",
    },
    {
      name: "matchType",
      type: "radio",
      label: "Match type",
      options: [
        { value: "exact", label: "Exact match" },
        { value: "case_insensitive", label: "Case-insensitive" },
      ],
      defaultValue: "exact",
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields,
    mode: "onChange",
    defaultValues: formData,
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    const newFormData = { ...formData, [fieldName]: value };
    setFormData(newFormData);
    updatePreview(newFormData);
  };

  const updatePreview = (data: any) => {
    if (!data.textToRemove) {
      setPreviewData((prev) => ({ ...prev, after: [...prev.before] }));
      return;
    }

    const results = previewData.before.map((item) => {
      let result = item;
      switch (data.matchType) {
        case "exact":
          result = item.split(data.textToRemove).join("");
          break;
        case "case_insensitive":
          const regex = new RegExp(
            data.textToRemove.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
            "gi"
          );
          result = item.replace(regex, "");
          break;
      }
      return result;
    });

    setPreviewData((prev) => ({ ...prev, after: results }));
  };

  const handleApply = () => {
    if (!formData.textToRemove) return;

    onApplyTransformation({
      targetField: toSnakeCase(targetField),
      transformation: {
        type: "remove_substring",
        order: 1,
        config: {
          text_to_remove: formData.textToRemove,
          match_type: formData.matchType,
        },
      },
    });
    onClose();
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Remove Substring Configuration"
      className="max-w-4xl w-full max-h-[90vh] flex flex-col"
    >
      {/* Body */}
      <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
        <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 p-4 rounded-md">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Remove Text:</strong> Clean data by removing unwanted
            prefixes, suffixes, or text patterns. Supports exact match or
            case-insensitive matching.
          </div>
        </div>

        <DynamicForm
          fields={fields}
          form={form}
          config={{
            fields,
            layout: "vertical",
            showSubmitButton: false,
            showResetButton: false,
            classes: FIELD_CLASSES,
          }}
          onFieldChange={handleFieldChange}
          gridColumns={1}
        />

        {/* Preview */}
        <div className="border rounded-lg p-4 bg-gray-50 relative">
          <h4 className="font-medium mb-3 text-gray-900">Preview</h4>
          <div className="grid grid-cols-2 gap-6 relative">
            {/* Before */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Before</p>
              <div className="space-y-2">
                {previewData.before.length > 0 ? (
                  previewData.before.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-3 rounded-md border border-gray-200"
                    >
                      <span className="text-sm text-gray-800">{item}</span>
                    </div>
                  ))
                ) : (
                  <div className="bg-white p-3 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-400">
                      No data available
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* After */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">After</p>
              <div className="space-y-2">
                {previewData.after.length > 0
                  ? previewData.after.map((item, idx) => (
                      <div
                        key={idx}
                        className="bg-white p-3 rounded-md border border-gray-200"
                      >
                        <span className="text-sm text-gray-800">{item}</span>
                      </div>
                    ))
                  : previewData.before.map((_, idx) => (
                      <div
                        key={idx}
                        className="bg-white p-3 rounded-md border border-gray-200"
                      >
                        <span className="text-sm text-gray-400">
                          {formData.textToRemove
                            ? "Processing..."
                            : "Enter text to remove"}
                        </span>
                      </div>
                    ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!formData.textToRemove}>
            Apply Transformation
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};

export default RemoveSubstringTransformationDialog;
